import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { useWriterComponentState } from "hooks";

import { ClientAppEvent } from "../../../../../../constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipmentId: number;
  shipmentType: ShipmentType;
  onDeleted: () => void;
}

function isProjectShipment(shipmentType: ShipmentType): boolean {
  return (
    shipmentType === "MULTI_SHIPMENT_PROJECT" ||
    shipmentType === "MULTI_SHIPMENT_PROJECT_CHILD"
  );
}

export function DeleteSuggestedRouteModal(props: Props) {
  const { isOpen, onClose, shipmentId, shipmentType, onDeleted } = props;
  const posthog = usePostHog();
  const { loading, error, write, resetState } = useWriterComponentState({
    errorMessage:
      "Failed to hide the map and route distance. Please contact us if the issue persists.",
    writeConfig: {
      method: "delete",
      url: `/shipments/${shipmentId}/suggested-route`,
    },
  });

  // Clear any prior error/loading before closing so a failed delete does not
  // resurface the next time the (still-mounted) dialog is reopened.
  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDelete = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.SUGGESTED_ROUTE_DELETED, {
        shipment_id: shipmentId,
        shipment_type: shipmentType,
      });
      onDeleted();
      handleClose();
    });
  };

  return (
    <Dialog fullWidth open={isOpen} onClose={handleClose}>
      <DialogTitle component="div">
        <Typography variant="h4">Hide Map & Route Distance</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Sometimes the map can show incorrect routes. If the route on the map
          does not appear to be correct, you can hide the map and route distance
          so carriers do not see it. Carriers will still see the route/stops you
          created and all shipment details. Your shipment will still be active
          and carriers can still quote it. You will simply be hiding the map and
          estimated route distance.
          {isProjectShipment(shipmentType) && (
            <b>
              {" "}
              Note: the map is shared across every load in this project, so
              hiding it hides the map and route distance for the entire project.
            </b>
          )}
        </Typography>
        <ErrorTypography error={error} />
      </DialogContent>
      <DialogActions>
        {loading ? (
          <Stack>
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <Tooltip title="Hide the map and estimated route distance from this shipment">
              <Button variant="contained" color="error" onClick={handleDelete}>
                Hide Map & Route Distance
              </Button>
            </Tooltip>
            <Tooltip title="Keep the map and route distance visible">
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
            </Tooltip>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
